import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useToast } from './Toast';
import { useAuth } from '../auth/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// ── Types ─────────────────────────────────────────────────────────────────────

type RuleAction = 'Allow' | 'Deny' | 'DNAT';
type RuleProtocol = 'Any' | 'TCP' | 'UDP' | 'ICMP' | 'ESP' | 'AH';
type RuleDirection = 'Inbound' | 'Outbound';
type RiskLevel = 'Low' | 'Medium' | 'High' | 'Critical';
type RuleCollectionType = 'Network' | 'Application' | 'Dnat';

interface FirewallRuleDto {
  id: string;
  name: string;
  ruleCollectionId: string;
  ruleCollectionGroupId: string;
  policyId: string;
  priority: number;
  protocol: RuleProtocol;
  sourceAddresses: string[];
  destinationAddresses: string[];
  destinationPorts: string[];
  sourceIpGroups: string[];
  destinationIpGroups: string[];
  targetFqdns: string[];
  action: RuleAction;
  isEnabled: boolean;
  description?: string;
  lastModified?: string;
}

interface RuleCollectionDto {
  id: string;
  name: string;
  rcgId: string;
  priority: number;
  type: RuleCollectionType;
  defaultAction: RuleAction;
  rules: FirewallRuleDto[];
}

interface RuleCollectionGroupDto {
  id: string;
  name: string;
  policyId: string;
  priority: number;
  ruleCollections: RuleCollectionDto[];
}

interface FirewallPolicyDto {
  id: string;
  name: string;
  resourceGroup: string;
  subscriptionId: string;
  location: string;
  threatIntelMode: string;
  ruleCollectionGroups: RuleCollectionGroupDto[];
}

interface NsgRuleDto {
  id: string;
  name: string;
  nsgId: string;
  priority: number;
  protocol: RuleProtocol;
  sourceAddressPrefix: string;
  destinationAddressPrefix: string;
  sourcePortRange: string;
  destinationPortRange: string;
  sourceAddressPrefixes?: string[];
  destinationAddressPrefixes?: string[];
  direction: RuleDirection;
  access: RuleAction;
  isDefault: boolean;
  description?: string;
  lastModified?: string;
}

interface NsgDto {
  id: string;
  name: string;
  resourceGroup: string;
  subscriptionId: string;
  location: string;
  associatedSubnet?: string;
  associatedNic?: string;
  securityRules: NsgRuleDto[];
  defaultSecurityRules: NsgRuleDto[];
}

interface FirewallRuleMatch {
  rule: FirewallRuleDto;
  policyName: string;
  rcgName: string;
  rcName: string;
  isSourceMatch: boolean;
  isDestinationMatch: boolean;
  risk: RiskLevel;
}

interface NsgRuleMatch {
  rule: NsgRuleDto;
  nsgName: string;
  isSourceMatch: boolean;
  isDestinationMatch: boolean;
  risk: RiskLevel;
}

interface IpSearchResult {
  ipAddress: string;
  firewallMatches: FirewallRuleMatch[];
  nsgMatches: NsgRuleMatch[];
  totalMatchCount: number;
}

interface ConflictDetail {
  type: string;
  description: string;
  affectedRuleIds: string[];
  severity: RiskLevel;
}

interface RuleConflictDto {
  resourceId: string;
  resourceName: string;
  resourceType: string;
  conflicts: ConflictDetail[];
}

interface BulkOperationResult {
  dryRun: boolean;
  successCount: number;
  failureCount: number;
  deletedRuleIds: string[];
  errors: string[];
}

// ── Risk scoring (client-side) ────────────────────────────────────────────────

function computeFirewallRisk(rule: FirewallRuleDto): RiskLevel {
  if (rule.action !== 'Allow') return 'Low';
  const anySource = rule.sourceAddresses.some(a => ['*', 'Any', '0.0.0.0/0'].includes(a));
  const anyDest = rule.destinationAddresses.some(a => ['*', 'Any', '0.0.0.0/0'].includes(a));
  const anyPort = rule.destinationPorts.some(p => ['*', 'Any'].includes(p));
  if (anySource && (anyDest || anyPort)) return 'Critical';
  if (anySource) return 'High';
  if (anyPort) return 'High';
  return 'Low';
}

function computeNsgRisk(rule: NsgRuleDto): RiskLevel {
  if (rule.access !== 'Allow') return 'Low';
  const anySource = ['*', 'Any', 'Internet', '0.0.0.0/0'].includes(rule.sourceAddressPrefix);
  const anyPort = ['*', 'Any'].includes(rule.destinationPortRange);
  if (anySource && anyPort) return 'Critical';
  if (anySource) return 'High';
  if (anyPort) return 'Medium';
  return 'Low';
}

// ── Styles ────────────────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius)',
  padding: '1rem 1.5rem',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};

const thStyle: React.CSSProperties = {
  background: 'var(--bg-table-header)',
  padding: '0.6rem 0.75rem',
  textAlign: 'left',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border-color)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color)',
  verticalAlign: 'middle',
};

const btnStyle: React.CSSProperties = {
  padding: '0.4rem 0.9rem',
  borderRadius: 'var(--border-radius)',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontWeight: 500,
};

const primaryBtn: React.CSSProperties = { ...btnStyle, background: 'var(--primary-color)', color: '#fff' };
const dangerBtn: React.CSSProperties = { ...btnStyle, background: 'var(--danger-color)', color: '#fff' };
const secondaryBtn: React.CSSProperties = { ...btnStyle, background: 'var(--bg-table-header)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' };

function riskBadge(risk: RiskLevel) {
  const colors: Record<RiskLevel, string> = {
    Critical: '#dc3545',
    High: '#fd7e14',
    Medium: '#ffc107',
    Low: '#28a745',
  };
  return (
    <span style={{
      background: colors[risk],
      color: risk === 'Medium' ? '#000' : '#fff',
      borderRadius: '4px',
      padding: '1px 7px',
      fontSize: '0.75rem',
      fontWeight: 700,
    }}>
      {risk}
    </span>
  );
}

function actionBadge(action: RuleAction) {
  const colors: Record<RuleAction, string> = { Allow: '#28a745', Deny: '#dc3545', DNAT: '#6f42c1' };
  return (
    <span style={{ background: colors[action], color: '#fff', borderRadius: '4px', padding: '1px 7px', fontSize: '0.75rem', fontWeight: 700 }}>
      {action}
    </span>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

interface ModalProps { title: string; onClose: () => void; children: React.ReactNode }
function Modal({ title, onClose, children }: ModalProps) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', width: '560px', maxHeight: '85vh', overflow: 'auto', padding: '1.5rem', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '1.4rem', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

interface FormRowProps { label: string; children: React.ReactNode }
function FormRow({ label, children }: FormRowProps) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.4rem 0.6rem', background: 'var(--bg-color)', border: '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box',
};

const textareaStyle: React.CSSProperties = { ...inputStyle, resize: 'vertical', minHeight: '60px', fontFamily: 'monospace' };
const selectStyle: React.CSSProperties = { ...inputStyle };

// ── Firewall Rule Modal ───────────────────────────────────────────────────────

interface FwRuleModalProps {
  policy: FirewallPolicyDto;
  existing?: FirewallRuleDto;
  onClose: () => void;
  onSave: (rcgId: string, rcId: string, rule: Partial<FirewallRuleDto>, dryRun: boolean) => void;
}

function FirewallRuleModal({ policy, existing, onClose, onSave }: FwRuleModalProps) {
  const allRcs = policy.ruleCollectionGroups.flatMap(rcg =>
    rcg.ruleCollections.map(rc => ({ rcgId: rcg.id, rcgName: rcg.name, rcId: rc.id, rcName: rc.name }))
  );

  const [rcgId, setRcgId] = useState(existing?.ruleCollectionGroupId || allRcs[0]?.rcgId || '');
  const [rcId, setRcId] = useState(existing?.ruleCollectionId || allRcs[0]?.rcId || '');
  const [name, setName] = useState(existing?.name || '');
  const [priority, setPriority] = useState(String(existing?.priority || 500));
  const [protocol, setProtocol] = useState<RuleProtocol>(existing?.protocol || 'TCP');
  const [action, setAction] = useState<RuleAction>(existing?.action || 'Allow');
  const [srcAddrs, setSrcAddrs] = useState((existing?.sourceAddresses || ['10.0.0.0/8']).join('\n'));
  const [dstAddrs, setDstAddrs] = useState((existing?.destinationAddresses || ['*']).join('\n'));
  const [dstPorts, setDstPorts] = useState((existing?.destinationPorts || ['443']).join('\n'));
  const [targetFqdns, setTargetFqdns] = useState((existing?.targetFqdns || []).join('\n'));
  const [description, setDescription] = useState(existing?.description || '');
  const [isEnabled, setIsEnabled] = useState(existing?.isEnabled !== false);
  const [dryRun, setDryRun] = useState(false);

  const filteredRcs = allRcs.filter(r => r.rcgId === rcgId);

  function handleRcgChange(newRcgId: string) {
    setRcgId(newRcgId);
    const first = allRcs.find(r => r.rcgId === newRcgId);
    if (first) setRcId(first.rcId);
  }

  function splitLines(s: string) { return s.split(/[\n,]+/).map(x => x.trim()).filter(Boolean); }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave(rcgId, rcId, {
      name, priority: parseInt(priority), protocol, action,
      sourceAddresses: splitLines(srcAddrs),
      destinationAddresses: splitLines(dstAddrs),
      destinationPorts: splitLines(dstPorts),
      targetFqdns: splitLines(targetFqdns),
      description, isEnabled,
    }, dryRun);
  }

  return (
    <Modal title={existing ? 'Edit Firewall Rule' : 'Add Firewall Rule'} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        {!existing && (
          <>
            <FormRow label="Rule Collection Group">
              <select style={selectStyle} value={rcgId} onChange={e => handleRcgChange(e.target.value)}>
                {policy.ruleCollectionGroups.map(rcg => <option key={rcg.id} value={rcg.id}>{rcg.name}</option>)}
              </select>
            </FormRow>
            <FormRow label="Rule Collection">
              <select style={selectStyle} value={rcId} onChange={e => setRcId(e.target.value)}>
                {filteredRcs.map(r => <option key={r.rcId} value={r.rcId}>{r.rcName}</option>)}
              </select>
            </FormRow>
          </>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <FormRow label="Name *">
            <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} required />
          </FormRow>
          <FormRow label="Priority (100–64999) *">
            <input style={inputStyle} type="number" min={100} max={64999} value={priority} onChange={e => setPriority(e.target.value)} required />
          </FormRow>
          <FormRow label="Protocol">
            <select style={selectStyle} value={protocol} onChange={e => setProtocol(e.target.value as RuleProtocol)}>
              {(['Any', 'TCP', 'UDP', 'ICMP', 'ESP', 'AH'] as RuleProtocol[]).map(p => <option key={p}>{p}</option>)}
            </select>
          </FormRow>
          <FormRow label="Action">
            <select style={selectStyle} value={action} onChange={e => setAction(e.target.value as RuleAction)}>
              {(['Allow', 'Deny', 'DNAT'] as RuleAction[]).map(a => <option key={a}>{a}</option>)}
            </select>
          </FormRow>
        </div>
        <FormRow label="Source Addresses (one per line or comma-separated)">
          <textarea style={textareaStyle} value={srcAddrs} onChange={e => setSrcAddrs(e.target.value)} />
        </FormRow>
        <FormRow label="Destination Addresses">
          <textarea style={textareaStyle} value={dstAddrs} onChange={e => setDstAddrs(e.target.value)} />
        </FormRow>
        <FormRow label="Destination Ports (e.g. 443, 80, 8080-8090)">
          <textarea style={{ ...textareaStyle, minHeight: '45px' }} value={dstPorts} onChange={e => setDstPorts(e.target.value)} />
        </FormRow>
        <FormRow label="Target FQDNs (Application rules only, one per line)">
          <textarea style={{ ...textareaStyle, minHeight: '45px' }} value={targetFqdns} onChange={e => setTargetFqdns(e.target.value)} />
        </FormRow>
        <FormRow label="Description">
          <input style={inputStyle} value={description} onChange={e => setDescription(e.target.value)} />
        </FormRow>
        <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={isEnabled} onChange={e => setIsEnabled(e.target.checked)} />
            Rule enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
            Dry run (preview only)
          </label>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="submit" style={dryRun ? secondaryBtn : primaryBtn}>
            {dryRun ? '👁 Preview' : existing ? 'Save Changes' : 'Add Rule'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── NSG Rule Modal ────────────────────────────────────────────────────────────

interface NsgRuleModalProps {
  nsgId: string;
  existing?: NsgRuleDto;
  onClose: () => void;
  onSave: (rule: Partial<NsgRuleDto>, dryRun: boolean) => void;
}

function NsgRuleModal({ nsgId, existing, onClose, onSave }: NsgRuleModalProps) {
  const [name, setName] = useState(existing?.name || '');
  const [priority, setPriority] = useState(String(existing?.priority || 500));
  const [protocol, setProtocol] = useState<RuleProtocol>(existing?.protocol || 'TCP');
  const [direction, setDirection] = useState<RuleDirection>(existing?.direction || 'Inbound');
  const [access, setAccess] = useState<RuleAction>(existing?.access || 'Allow');
  const [srcPrefix, setSrcPrefix] = useState(existing?.sourceAddressPrefix || '*');
  const [dstPrefix, setDstPrefix] = useState(existing?.destinationAddressPrefix || '*');
  const [srcPort, setSrcPort] = useState(existing?.sourcePortRange || '*');
  const [dstPort, setDstPort] = useState(existing?.destinationPortRange || '*');
  const [description, setDescription] = useState(existing?.description || '');
  const [dryRun, setDryRun] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({
      name, priority: parseInt(priority), protocol, direction, access,
      sourceAddressPrefix: srcPrefix, destinationAddressPrefix: dstPrefix,
      sourcePortRange: srcPort, destinationPortRange: dstPort,
      description, nsgId,
    }, dryRun);
  }

  return (
    <Modal title={existing ? 'Edit NSG Rule' : 'Add NSG Rule'} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <FormRow label="Name *">
            <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} required />
          </FormRow>
          <FormRow label="Priority (100–4096) *">
            <input style={inputStyle} type="number" min={100} max={4096} value={priority} onChange={e => setPriority(e.target.value)} required />
          </FormRow>
          <FormRow label="Protocol">
            <select style={selectStyle} value={protocol} onChange={e => setProtocol(e.target.value as RuleProtocol)}>
              {(['Any', 'TCP', 'UDP', 'ICMP'] as RuleProtocol[]).map(p => <option key={p}>{p}</option>)}
            </select>
          </FormRow>
          <FormRow label="Direction">
            <select style={selectStyle} value={direction} onChange={e => setDirection(e.target.value as RuleDirection)}>
              <option>Inbound</option>
              <option>Outbound</option>
            </select>
          </FormRow>
          <FormRow label="Access">
            <select style={selectStyle} value={access} onChange={e => setAccess(e.target.value as RuleAction)}>
              <option>Allow</option>
              <option>Deny</option>
            </select>
          </FormRow>
          <FormRow label="Source Port Range">
            <input style={inputStyle} value={srcPort} onChange={e => setSrcPort(e.target.value)} placeholder="* or 1024-65535" />
          </FormRow>
          <FormRow label="Source Address Prefix">
            <input style={inputStyle} value={srcPrefix} onChange={e => setSrcPrefix(e.target.value)} placeholder="* / 10.0.0.0/8 / VirtualNetwork" />
          </FormRow>
          <FormRow label="Destination Port Range">
            <input style={inputStyle} value={dstPort} onChange={e => setDstPort(e.target.value)} placeholder="443 or 8080-8090" />
          </FormRow>
          <FormRow label="Destination Address Prefix">
            <input style={inputStyle} value={dstPrefix} onChange={e => setDstPrefix(e.target.value)} placeholder="* / 10.2.0.0/24 / Internet" />
          </FormRow>
        </div>
        <FormRow label="Description">
          <input style={inputStyle} value={description} onChange={e => setDescription(e.target.value)} />
        </FormRow>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
            Dry run (preview only)
          </label>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="submit" style={dryRun ? secondaryBtn : primaryBtn}>
            {dryRun ? '👁 Preview' : existing ? 'Save Changes' : 'Add Rule'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Delete Confirm Modal ──────────────────────────────────────────────────────

interface DeleteConfirmProps {
  ruleNames: string[];
  onCancel: () => void;
  onConfirm: (dryRun: boolean) => void;
}

function DeleteConfirmModal({ ruleNames, onCancel, onConfirm }: DeleteConfirmProps) {
  const [dryRun, setDryRun] = useState(true);
  return (
    <Modal title="Confirm Deletion" onClose={onCancel}>
      <p style={{ color: 'var(--text-secondary)', marginTop: 0 }}>
        You are about to delete <strong style={{ color: 'var(--text-primary)' }}>{ruleNames.length} rule{ruleNames.length !== 1 ? 's' : ''}</strong>:
      </p>
      <ul style={{ maxHeight: '160px', overflowY: 'auto', padding: '0 0 0 1.2rem', margin: '0 0 1rem' }}>
        {ruleNames.map(n => <li key={n} style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{n}</li>)}
      </ul>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
        <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
        Dry run first (preview without deleting)
      </label>
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
        <button style={secondaryBtn} onClick={onCancel}>Cancel</button>
        <button style={dryRun ? secondaryBtn : dangerBtn} onClick={() => onConfirm(dryRun)}>
          {dryRun ? '👁 Preview deletion' : '🗑 Delete now'}
        </button>
      </div>
    </Modal>
  );
}

// ── Conflict Panel ────────────────────────────────────────────────────────────

const conflictTypeMeta: Record<string, { icon: string; label: string; color: string; bg: string }> = {
  DuplicatePriority:  { icon: '⚡', label: 'Duplicate Priority', color: '#fd7e14', bg: '#2d1a0a' },
  AnySourceAllow:     { icon: '🌐', label: 'Any-Source Allow',   color: '#dc3545', bg: '#2d1010' },
  ShadowedRule:       { icon: '👻', label: 'Shadowed Rule',      color: '#ffc107', bg: '#2d2600' },
  MutualRedundancy:   { icon: '🔄', label: 'Identical Rules',    color: '#6f42c1', bg: '#1e1428' },
  RedundantRule:      { icon: '🔁', label: 'Redundant Rule',     color: '#20c997', bg: '#0d2620' },
};

function conflictTypeLabel(type: string) {
  const meta = conflictTypeMeta[type] ?? { icon: '⚠', label: type, color: '#ffc107', bg: '#2d2600' };
  return (
    <span style={{ background: meta.color + '22', border: `1px solid ${meta.color}44`, color: meta.color, borderRadius: '4px', padding: '1px 6px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {meta.icon} {meta.label}
    </span>
  );
}

function ConflictPanel({ conflicts, onDismiss }: { conflicts: RuleConflictDto[]; onDismiss: () => void }) {
  if (conflicts.length === 0) return null;

  const allDetails = conflicts.flatMap(c => c.conflicts);
  const redundancyCount = allDetails.filter(d => d.type === 'RedundantRule' || d.type === 'MutualRedundancy').length;
  const securityCount = allDetails.filter(d => d.type !== 'RedundantRule' && d.type !== 'MutualRedundancy').length;
  const totalIssues = allDetails.length;

  const severityColors: Record<RiskLevel, string> = { Critical: '#dc3545', High: '#fd7e14', Medium: '#ffc107', Low: '#28a745' };

  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', padding: '1rem', marginBottom: '1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <strong>Analysis Results: {totalIssues} issue{totalIssues !== 1 ? 's' : ''} across {conflicts.length} resource{conflicts.length !== 1 ? 's' : ''}</strong>
          {securityCount > 0 && (
            <span style={{ color: '#dc3545', fontSize: '0.82rem' }}>⚠ {securityCount} security issue{securityCount !== 1 ? 's' : ''}</span>
          )}
          {redundancyCount > 0 && (
            <span style={{ color: '#20c997', fontSize: '0.82rem' }}>🔁 {redundancyCount} redundant rule{redundancyCount !== 1 ? 's' : ''} can be removed</span>
          )}
        </div>
        <button style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '1.2rem' }} onClick={onDismiss}>×</button>
      </div>

      {/* Per-resource breakdown */}
      {conflicts.map(c => (
        <div key={c.resourceId} style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
            {c.resourceType === 'firewall' ? '🔥' : '🛡'} <strong style={{ color: 'var(--text-primary)' }}>{c.resourceName}</strong>
            <span style={{ marginLeft: '0.5rem', fontSize: '0.78rem' }}>({c.conflicts.length} issue{c.conflicts.length !== 1 ? 's' : ''})</span>
          </div>
          {c.conflicts.map((cd, i) => (
            <div key={i} style={{
              display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
              marginLeft: '1rem', marginBottom: '0.35rem',
              padding: '0.35rem 0.6rem',
              background: (conflictTypeMeta[cd.type] ?? conflictTypeMeta.ShadowedRule).bg,
              borderRadius: '4px',
              borderLeft: `3px solid ${severityColors[cd.severity]}`,
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: 1 }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {conflictTypeLabel(cd.type)}
                  <span style={{ fontSize: '0.72rem', color: severityColors[cd.severity], fontWeight: 700 }}>{cd.severity}</span>
                </div>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{cd.description}</span>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Redundancy summary callout */}
      {redundancyCount > 0 && (
        <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.75rem', background: '#0d2620', border: '1px solid #20c99744', borderRadius: 'var(--border-radius)', fontSize: '0.82rem', color: '#20c997' }}>
          💡 <strong>{redundancyCount} redundant rule{redundancyCount !== 1 ? 's' : ''}</strong> detected — removing them will reduce your rule count without affecting traffic behaviour.
          Use bulk-delete with dry-run to preview the changes first.
        </div>
      )}
    </div>
  );
}

// ── CSV Export ────────────────────────────────────────────────────────────────

function exportCsv(rows: string[][], filename: string) {
  const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function exportFirewallRules(rules: FirewallRuleDto[], policyName: string) {
  const header = ['Priority', 'Name', 'Protocol', 'Source Addresses', 'Destination Addresses', 'Destination Ports', 'Target FQDNs', 'Action', 'Enabled', 'Description'];
  const rows = rules.map(r => [
    String(r.priority), r.name, r.protocol,
    r.sourceAddresses.join('; '), r.destinationAddresses.join('; '), r.destinationPorts.join('; '),
    r.targetFqdns.join('; '), r.action, String(r.isEnabled), r.description || '',
  ]);
  exportCsv([header, ...rows], `fw-rules-${policyName}-${new Date().toISOString().slice(0, 10)}.csv`);
}

function exportNsgRules(rules: NsgRuleDto[], nsgName: string) {
  const header = ['Priority', 'Name', 'Protocol', 'Direction', 'Source Address', 'Source Port', 'Destination Address', 'Destination Port', 'Access', 'Description'];
  const rows = rules.map(r => [
    String(r.priority), r.name, r.protocol, r.direction,
    r.sourceAddressPrefix, r.sourcePortRange, r.destinationAddressPrefix, r.destinationPortRange,
    r.access, r.description || '',
  ]);
  exportCsv([header, ...rows], `nsg-rules-${nsgName}-${new Date().toISOString().slice(0, 10)}.csv`);
}

// ── Firewall Tab ──────────────────────────────────────────────────────────────

interface FirewallTabProps {
  policies: FirewallPolicyDto[];
  subscriptionId: string;
  onRefresh: () => void;
  showToast: (msg: string, type?: 'success' | 'error' | 'info') => void;
}

function FirewallTab({ policies, subscriptionId, onRefresh, showToast }: FirewallTabProps) {
  const { user } = useAuth();
  // Admins execute directly; operators submit four-eyes change requests.
  const isAdmin = user?.role === 'admin';
  const canWrite = isAdmin || user?.role === 'operator';
  const [selectedPolicyId, setSelectedPolicyId] = useState(policies[0]?.id || '');
  const [selectedRcgId, setSelectedRcgId] = useState('');
  const [selectedRcId, setSelectedRcId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [addModal, setAddModal] = useState(false);
  const [editRule, setEditRule] = useState<FirewallRuleDto | null>(null);
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);
  const [conflicts, setConflicts] = useState<RuleConflictDto[]>([]);
  const [conflictsVisible, setConflictsVisible] = useState(false);
  const [conflictsLoading, setConflictsLoading] = useState(false);

  const policy = policies.find(p => p.id === selectedPolicyId);

  // Keep the RCG/RC selection across refreshes; reset only when it no longer exists
  // (the policy object's identity changes on every reload, so resetting on `policy`
  // snapped the view back to the first collection after each rule save).
  useEffect(() => {
    if (!policy) return;
    const currentRcg = policy.ruleCollectionGroups.find(g => g.id === selectedRcgId);
    if (!currentRcg) {
      const firstRcg = policy.ruleCollectionGroups[0];
      setSelectedRcgId(firstRcg?.id || '');
      setSelectedRcId(firstRcg?.ruleCollections[0]?.id || '');
      return;
    }
    if (!currentRcg.ruleCollections.some(c => c.id === selectedRcId)) {
      setSelectedRcId(currentRcg.ruleCollections[0]?.id || '');
    }
  }, [policy, selectedRcgId, selectedRcId]);

  const rcg = policy?.ruleCollectionGroups.find(g => g.id === selectedRcgId);
  const rc = rcg?.ruleCollections.find(c => c.id === selectedRcId);
  const rules = rc?.rules || [];

  const stats = useMemo(() => {
    const all = policy?.ruleCollectionGroups.flatMap(g => g.ruleCollections.flatMap(c => c.rules)) || [];
    return {
      total: all.length,
      allow: all.filter(r => r.action === 'Allow').length,
      deny: all.filter(r => r.action === 'Deny').length,
      highRisk: all.filter(r => ['High', 'Critical'].includes(computeFirewallRisk(r))).length,
    };
  }, [policy]);

  async function handleDetectConflicts() {
    setConflictsLoading(true);
    try {
      const res = await axios.get<RuleConflictDto[]>(`${API_URL}/networksecurity/conflicts`, { params: { subscriptionId } });
      const fwConflicts = res.data.filter(c => c.resourceType === 'firewall');
      setConflicts(fwConflicts);
      setConflictsVisible(true);
      if (fwConflicts.length === 0) showToast('No conflicts detected in firewall policies.', 'success');
    } catch {
      showToast('Failed to detect conflicts', 'error');
    } finally {
      setConflictsLoading(false);
    }
  }

  async function handleSaveRule(rcgId: string, rcId: string, rule: Partial<FirewallRuleDto>, dryRun: boolean) {
    try {
      if (!isAdmin) {
        if (dryRun) {
          showToast(`Dry run: rule "${rule.name}" — submit to request approval.`, 'info');
          return;
        }
        await axios.post(`${API_URL}/approvals`, editRule ? {
          operationType: 'UpdateFirewallRule',
          targetSummary: `Update firewall rule "${editRule.name}" in policy "${policy?.name}"`,
          reason: '',
          payload: { policyId: selectedPolicyId, ruleId: editRule.id, rule },
        } : {
          operationType: 'CreateFirewallRule',
          targetSummary: `Add firewall rule "${rule.name}" to policy "${policy?.name}"`,
          reason: '',
          payload: { policyId: selectedPolicyId, rcgId, rcId, rule },
        });
        showToast('Change request submitted for approval.', 'info');
        setAddModal(false);
        setEditRule(null);
        return;
      }
      if (editRule) {
        const res = await axios.put<FirewallRuleDto>(`${API_URL}/networksecurity/firewall/policies/${encodeURIComponent(selectedPolicyId)}/rules/${encodeURIComponent(editRule.id)}`, rule);
        showToast(`Rule "${res.data.name}" updated.`, 'success');
      } else {
        if (dryRun) {
          showToast(`Dry run: Rule "${rule.name}" would be added to RC "${rc?.name}".`, 'info');
          setAddModal(false);
          return;
        }
        await axios.post(`${API_URL}/networksecurity/firewall/policies/${encodeURIComponent(selectedPolicyId)}/rules`, { rcgId, rcId, rule });
        showToast(`Rule "${rule.name}" added.`, 'success');
      }
      setAddModal(false);
      setEditRule(null);
      onRefresh();
    } catch {
      showToast('Failed to save rule', 'error');
    }
  }

  async function handleDelete(ruleIds: string[], dryRun: boolean) {
    try {
      if (!isAdmin) {
        if (dryRun) {
          showToast(`Dry run: ${ruleIds.length} rule(s) would be deleted — submit to request approval.`, 'info');
          return;
        }
        await axios.post(`${API_URL}/approvals`, ruleIds.length === 1 ? {
          operationType: 'DeleteFirewallRule',
          targetSummary: `Delete firewall rule "${rules.find(r => r.id === ruleIds[0])?.name || ruleIds[0]}" from policy "${policy?.name}"`,
          reason: '',
          payload: { policyId: selectedPolicyId, ruleId: ruleIds[0] },
        } : {
          operationType: 'BulkDeleteRules',
          targetSummary: `Bulk delete ${ruleIds.length} firewall rules from policy "${policy?.name}"`,
          reason: '',
          payload: { resourceType: 'firewall', resourceId: selectedPolicyId, ruleIds, dryRun: false },
        });
        showToast('Delete request submitted for approval.', 'info');
        setSelected(new Set());
        setDeleteIds(null);
        return;
      }
      if (ruleIds.length === 1) {
        const res = await axios.delete(`${API_URL}/networksecurity/firewall/policies/${encodeURIComponent(selectedPolicyId)}/rules/${encodeURIComponent(ruleIds[0])}`, { params: { dryRun } });
        if (dryRun) { showToast(res.data.message, 'info'); }
        else { showToast('Rule deleted.', 'success'); setSelected(new Set()); onRefresh(); }
      } else {
        const res = await axios.post<BulkOperationResult>(`${API_URL}/networksecurity/bulk/delete`, {
          resourceType: 'firewall', resourceId: selectedPolicyId, ruleIds, dryRun,
        });
        if (dryRun) {
          showToast(`Dry run: ${res.data.successCount} rules would be deleted.`, 'info');
        } else {
          showToast(`${res.data.successCount} rules deleted${res.data.failureCount > 0 ? `, ${res.data.failureCount} failed` : ''}.`, res.data.failureCount > 0 ? 'error' : 'success');
          setSelected(new Set()); onRefresh();
        }
      }
    } catch {
      showToast('Delete operation failed', 'error');
    } finally {
      setDeleteIds(null);
    }
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <select style={{ ...selectStyle, width: 'auto', minWidth: '200px' }} value={selectedPolicyId} onChange={e => setSelectedPolicyId(e.target.value)}>
          {policies.map(p => <option key={p.id} value={p.id}>{p.name} ({p.location})</option>)}
        </select>
        {policy && <>
          <select style={{ ...selectStyle, width: 'auto', minWidth: '160px' }} value={selectedRcgId} onChange={e => { setSelectedRcgId(e.target.value); const rcg2 = policy.ruleCollectionGroups.find(g => g.id === e.target.value); setSelectedRcId(rcg2?.ruleCollections[0]?.id || ''); }}>
            {policy.ruleCollectionGroups.map(g => <option key={g.id} value={g.id}>{g.name} (P{g.priority})</option>)}
          </select>
          <select style={{ ...selectStyle, width: 'auto', minWidth: '160px' }} value={selectedRcId} onChange={e => setSelectedRcId(e.target.value)}>
            {rcg?.ruleCollections.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
          </select>
        </>}
        {canWrite && <button style={primaryBtn} onClick={() => setAddModal(true)}>+ Add Rule</button>}
        <button style={secondaryBtn} onClick={onRefresh}>↻ Refresh</button>
        <button style={secondaryBtn} onClick={handleDetectConflicts} disabled={conflictsLoading}>
          {conflictsLoading ? 'Scanning...' : '⚠ Detect Conflicts'}
        </button>
        {rules.length > 0 && (
          <button style={secondaryBtn} onClick={() => exportFirewallRules(rules, policy?.name || 'policy')}>⬇ Export CSV</button>
        )}
        {canWrite && selected.size > 0 && (
          <button style={dangerBtn} onClick={() => setDeleteIds(Array.from(selected))}>
            🗑 Delete Selected ({selected.size})
          </button>
        )}
      </div>

      {/* Stats */}
      {policy && (
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Total Rules', value: stats.total, color: 'var(--text-primary)' },
            { label: 'Allow', value: stats.allow, color: '#28a745' },
            { label: 'Deny', value: stats.deny, color: '#dc3545' },
            { label: 'High Risk', value: stats.highRisk, color: '#fd7e14' },
          ].map(s => (
            <div key={s.label} style={{ ...cardStyle, flex: '1', minWidth: '120px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {conflictsVisible && (
        <ConflictPanel conflicts={conflicts} onDismiss={() => setConflictsVisible(false)} />
      )}

      {/* Rules table */}
      <div style={cardStyle}>
        {rc ? (
          <div style={{ marginBottom: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            {rc.type} collection · Default action: <strong style={{ color: 'var(--text-primary)' }}>{rc.defaultAction}</strong>
            {rules.length === 0 && <span style={{ marginLeft: '1rem' }}>No rules in this collection.</span>}
          </div>
        ) : (
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Select a rule collection above.</div>
        )}
        {rules.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}><input type="checkbox" checked={selected.size === rules.length} onChange={e => setSelected(e.target.checked ? new Set(rules.map(r => r.id)) : new Set())} /></th>
                  <th style={thStyle}>Priority</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Protocol</th>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Destination</th>
                  <th style={thStyle}>Ports / FQDNs</th>
                  <th style={thStyle}>Action</th>
                  <th style={thStyle}>Risk</th>
                  <th style={thStyle}>Enabled</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...rules].sort((a, b) => a.priority - b.priority).map(rule => {
                  const risk = computeFirewallRisk(rule);
                  const rowBg = !rule.isEnabled ? 'rgba(255,255,255,0.03)' : rule.action === 'Deny' ? 'rgba(220,53,69,0.06)' : undefined;
                  return (
                    <tr key={rule.id} style={{ background: rowBg, opacity: rule.isEnabled ? 1 : 0.5 }}>
                      <td style={tdStyle}><input type="checkbox" checked={selected.has(rule.id)} onChange={e => { const s = new Set(selected); e.target.checked ? s.add(rule.id) : s.delete(rule.id); setSelected(s); }} /></td>
                      <td style={tdStyle}><code>{rule.priority}</code></td>
                      <td style={{ ...tdStyle, maxWidth: '160px' }}>
                        <div style={{ fontWeight: 500, textDecoration: rule.isEnabled ? undefined : 'line-through' }}>{rule.name}</div>
                        {rule.description && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{rule.description}</div>}
                      </td>
                      <td style={tdStyle}>{rule.protocol}</td>
                      <td style={{ ...tdStyle, maxWidth: '140px', fontFamily: 'monospace', fontSize: '0.8rem' }}>{rule.sourceAddresses.join(', ')}</td>
                      <td style={{ ...tdStyle, maxWidth: '140px', fontFamily: 'monospace', fontSize: '0.8rem' }}>{rule.targetFqdns.length > 0 ? rule.targetFqdns.join(', ') : rule.destinationAddresses.join(', ')}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8rem' }}>{rule.destinationPorts.join(', ')}</td>
                      <td style={tdStyle}>{actionBadge(rule.action)}</td>
                      <td style={tdStyle}>{riskBadge(risk)}</td>
                      <td style={tdStyle}><span style={{ color: rule.isEnabled ? '#28a745' : 'var(--text-secondary)' }}>{rule.isEnabled ? '✓' : '✗'}</span></td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        {canWrite ? (
                          <>
                            <button style={{ ...secondaryBtn, padding: '0.2rem 0.5rem', marginRight: '0.25rem' }} title="Edit" onClick={() => setEditRule(rule)}>✏️</button>
                            <button style={{ ...dangerBtn, padding: '0.2rem 0.5rem' }} title="Delete" onClick={() => setDeleteIds([rule.id])}>🗑</button>
                          </>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {addModal && policy && (
        <FirewallRuleModal policy={policy} onClose={() => setAddModal(false)} onSave={handleSaveRule} />
      )}
      {editRule && policy && (
        <FirewallRuleModal policy={policy} existing={editRule} onClose={() => setEditRule(null)} onSave={handleSaveRule} />
      )}
      {deleteIds && (
        <DeleteConfirmModal
          ruleNames={deleteIds.map(id => rules.find(r => r.id === id)?.name || id)}
          onCancel={() => setDeleteIds(null)}
          onConfirm={dryRun => handleDelete(deleteIds, dryRun)}
        />
      )}
    </div>
  );
}

// ── NSG Tab ───────────────────────────────────────────────────────────────────

interface NsgTabProps {
  nsgs: NsgDto[];
  subscriptionId: string;
  onRefresh: () => void;
  showToast: (msg: string, type?: 'success' | 'error' | 'info') => void;
}

function NsgTab({ nsgs, subscriptionId, onRefresh, showToast }: NsgTabProps) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canWrite = isAdmin || user?.role === 'operator';
  const [selectedNsgId, setSelectedNsgId] = useState(nsgs[0]?.id || '');
  const [direction, setDirection] = useState<RuleDirection>('Inbound');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [addModal, setAddModal] = useState(false);
  const [editRule, setEditRule] = useState<NsgRuleDto | null>(null);
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);
  const [conflicts, setConflicts] = useState<RuleConflictDto[]>([]);
  const [conflictsVisible, setConflictsVisible] = useState(false);
  const [conflictsLoading, setConflictsLoading] = useState(false);
  const [showDefaults, setShowDefaults] = useState(false);

  const nsg = nsgs.find(n => n.id === selectedNsgId);

  const customRules = useMemo(() =>
    (nsg?.securityRules || []).filter(r => r.direction === direction).sort((a, b) => a.priority - b.priority),
    [nsg, direction]
  );

  const defaultRules = useMemo(() =>
    (nsg?.defaultSecurityRules || []).filter(r => r.direction === direction).sort((a, b) => a.priority - b.priority),
    [nsg, direction]
  );

  const allRules = showDefaults ? [...customRules, ...defaultRules] : customRules;

  async function handleDetectConflicts() {
    setConflictsLoading(true);
    try {
      const res = await axios.get<RuleConflictDto[]>(`${API_URL}/networksecurity/conflicts`, { params: { subscriptionId } });
      const nsgConflicts = res.data.filter(c => c.resourceType === 'nsg');
      setConflicts(nsgConflicts);
      setConflictsVisible(true);
      if (nsgConflicts.length === 0) showToast('No conflicts detected in NSGs.', 'success');
    } catch {
      showToast('Failed to detect conflicts', 'error');
    } finally {
      setConflictsLoading(false);
    }
  }

  async function handleSaveRule(rule: Partial<NsgRuleDto>, dryRun: boolean) {
    try {
      if (!isAdmin) {
        if (dryRun) {
          showToast(`Dry run: rule "${rule.name}" — submit to request approval.`, 'info');
          return;
        }
        await axios.post(`${API_URL}/approvals`, editRule ? {
          operationType: 'UpdateNsgRule',
          targetSummary: `Update NSG rule "${editRule.name}" in NSG "${nsg?.name}"`,
          reason: '',
          payload: { nsgId: selectedNsgId, ruleId: editRule.id, rule },
        } : {
          operationType: 'CreateNsgRule',
          targetSummary: `Add NSG rule "${rule.name}" to NSG "${nsg?.name}"`,
          reason: '',
          payload: { nsgId: selectedNsgId, rule },
        });
        showToast('Change request submitted for approval.', 'info');
        setAddModal(false);
        setEditRule(null);
        return;
      }
      if (editRule) {
        const res = await axios.put<NsgRuleDto>(`${API_URL}/networksecurity/nsgs/${encodeURIComponent(selectedNsgId)}/rules/${encodeURIComponent(editRule.id)}`, rule);
        showToast(`Rule "${res.data.name}" updated.`, 'success');
      } else {
        if (dryRun) {
          showToast(`Dry run: Rule "${rule.name}" would be added to NSG "${nsg?.name}".`, 'info');
          setAddModal(false);
          return;
        }
        await axios.post(`${API_URL}/networksecurity/nsgs/${encodeURIComponent(selectedNsgId)}/rules`, rule);
        showToast(`Rule "${rule.name}" added.`, 'success');
      }
      setAddModal(false);
      setEditRule(null);
      onRefresh();
    } catch {
      showToast('Failed to save NSG rule', 'error');
    }
  }

  async function handleDelete(ruleIds: string[], dryRun: boolean) {
    try {
      if (!isAdmin) {
        if (dryRun) {
          showToast(`Dry run: ${ruleIds.length} rule(s) would be deleted — submit to request approval.`, 'info');
          return;
        }
        await axios.post(`${API_URL}/approvals`, ruleIds.length === 1 ? {
          operationType: 'DeleteNsgRule',
          targetSummary: `Delete NSG rule "${customRules.find(r => r.id === ruleIds[0])?.name || ruleIds[0]}" from NSG "${nsg?.name}"`,
          reason: '',
          payload: { nsgId: selectedNsgId, ruleId: ruleIds[0] },
        } : {
          operationType: 'BulkDeleteRules',
          targetSummary: `Bulk delete ${ruleIds.length} NSG rules from NSG "${nsg?.name}"`,
          reason: '',
          payload: { resourceType: 'nsg', resourceId: selectedNsgId, ruleIds, dryRun: false },
        });
        showToast('Delete request submitted for approval.', 'info');
        setSelected(new Set());
        setDeleteIds(null);
        return;
      }
      if (ruleIds.length === 1) {
        const res = await axios.delete(`${API_URL}/networksecurity/nsgs/${encodeURIComponent(selectedNsgId)}/rules/${encodeURIComponent(ruleIds[0])}`, { params: { dryRun } });
        if (dryRun) { showToast(res.data.message, 'info'); }
        else { showToast('Rule deleted.', 'success'); setSelected(new Set()); onRefresh(); }
      } else {
        const res = await axios.post<BulkOperationResult>(`${API_URL}/networksecurity/bulk/delete`, {
          resourceType: 'nsg', resourceId: selectedNsgId, ruleIds, dryRun,
        });
        if (dryRun) {
          showToast(`Dry run: ${res.data.successCount} rules would be deleted.`, 'info');
        } else {
          showToast(`${res.data.successCount} rules deleted.`, 'success');
          setSelected(new Set()); onRefresh();
        }
      }
    } catch {
      showToast('Delete operation failed', 'error');
    } finally {
      setDeleteIds(null);
    }
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <select style={{ ...selectStyle, width: 'auto', minWidth: '200px' }} value={selectedNsgId} onChange={e => { setSelectedNsgId(e.target.value); setSelected(new Set()); }}>
          {nsgs.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
        </select>
        <button style={direction === 'Inbound' ? primaryBtn : secondaryBtn} onClick={() => setDirection('Inbound')}>⬇ Inbound</button>
        <button style={direction === 'Outbound' ? primaryBtn : secondaryBtn} onClick={() => setDirection('Outbound')}>⬆ Outbound</button>
        {canWrite && <button style={primaryBtn} onClick={() => setAddModal(true)}>+ Add Rule</button>}
        <button style={secondaryBtn} onClick={onRefresh}>↻ Refresh</button>
        <button style={secondaryBtn} onClick={handleDetectConflicts} disabled={conflictsLoading}>
          {conflictsLoading ? 'Scanning...' : '⚠ Detect Conflicts'}
        </button>
        <button style={showDefaults ? primaryBtn : secondaryBtn} onClick={() => setShowDefaults(!showDefaults)}>
          {showDefaults ? '🔒 Hide Defaults' : '🔒 Show Defaults'}
        </button>
        {customRules.length > 0 && (
          <button style={secondaryBtn} onClick={() => exportNsgRules([...customRules, ...defaultRules], nsg?.name || 'nsg')}>⬇ Export CSV</button>
        )}
        {canWrite && selected.size > 0 && (
          <button style={dangerBtn} onClick={() => setDeleteIds(Array.from(selected))}>
            🗑 Delete Selected ({selected.size})
          </button>
        )}
      </div>

      {/* NSG info */}
      {nsg && (
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {nsg.associatedSubnet && (
            <div style={{ ...cardStyle, flex: 1 }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Associated Subnet</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{nsg.associatedSubnet}</div>
            </div>
          )}
          {nsg.associatedNic && (
            <div style={{ ...cardStyle, flex: 1 }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Associated NIC</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{nsg.associatedNic}</div>
            </div>
          )}
          <div style={{ ...cardStyle, flex: 1, minWidth: '100px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{customRules.length}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{direction} Custom Rules</div>
          </div>
          <div style={{ ...cardStyle, flex: 1, minWidth: '100px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fd7e14' }}>
              {customRules.filter(r => ['High', 'Critical'].includes(computeNsgRisk(r))).length}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>High Risk</div>
          </div>
          <div style={{ ...cardStyle, flex: 1, minWidth: '100px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#28a745' }}>{customRules.filter(r => r.access === 'Allow').length}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Allow</div>
          </div>
          <div style={{ ...cardStyle, flex: 1, minWidth: '100px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#dc3545' }}>{customRules.filter(r => r.access === 'Deny').length}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Deny</div>
          </div>
        </div>
      )}

      {conflictsVisible && (
        <ConflictPanel conflicts={conflicts} onDismiss={() => setConflictsVisible(false)} />
      )}

      {/* Rules table */}
      <div style={cardStyle}>
        {allRules.length === 0 ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>No {direction.toLowerCase()} rules.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}><input type="checkbox" checked={selected.size > 0 && selected.size === customRules.length} onChange={e => setSelected(e.target.checked ? new Set(customRules.map(r => r.id)) : new Set())} /></th>
                  <th style={thStyle}>Priority</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Protocol</th>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Src Port</th>
                  <th style={thStyle}>Destination</th>
                  <th style={thStyle}>Dst Port</th>
                  <th style={thStyle}>Access</th>
                  <th style={thStyle}>Risk</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allRules.map(rule => {
                  const risk = computeNsgRisk(rule);
                  const rowBg = rule.isDefault ? 'rgba(255,255,255,0.02)' : rule.access === 'Deny' ? 'rgba(220,53,69,0.06)' : undefined;
                  return (
                    <tr key={rule.id} style={{ background: rowBg, opacity: rule.isDefault ? 0.65 : 1 }}>
                      <td style={tdStyle}>
                        {!rule.isDefault && <input type="checkbox" checked={selected.has(rule.id)} onChange={e => { const s = new Set(selected); e.target.checked ? s.add(rule.id) : s.delete(rule.id); setSelected(s); }} />}
                        {rule.isDefault && <span title="Azure default rule — cannot be modified">🔒</span>}
                      </td>
                      <td style={tdStyle}><code>{rule.priority}</code></td>
                      <td style={{ ...tdStyle, maxWidth: '180px' }}>
                        <div style={{ fontWeight: 500 }}>{rule.name}</div>
                        {rule.description && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{rule.description}</div>}
                      </td>
                      <td style={tdStyle}>{rule.protocol}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8rem' }}>{rule.sourceAddressPrefix}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8rem' }}>{rule.sourcePortRange}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8rem' }}>{rule.destinationAddressPrefix}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8rem' }}>{rule.destinationPortRange}</td>
                      <td style={tdStyle}>{actionBadge(rule.access)}</td>
                      <td style={tdStyle}>{riskBadge(risk)}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        {!rule.isDefault && canWrite ? (
                          <>
                            <button style={{ ...secondaryBtn, padding: '0.2rem 0.5rem', marginRight: '0.25rem' }} title="Edit" onClick={() => setEditRule(rule)}>✏️</button>
                            <button style={{ ...dangerBtn, padding: '0.2rem 0.5rem' }} title="Delete" onClick={() => setDeleteIds([rule.id])}>🗑</button>
                          </>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Platform</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {addModal && nsg && (
        <NsgRuleModal nsgId={selectedNsgId} onClose={() => setAddModal(false)} onSave={handleSaveRule} />
      )}
      {editRule && (
        <NsgRuleModal nsgId={selectedNsgId} existing={editRule} onClose={() => setEditRule(null)} onSave={handleSaveRule} />
      )}
      {deleteIds && (
        <DeleteConfirmModal
          ruleNames={deleteIds.map(id => customRules.find(r => r.id === id)?.name || id)}
          onCancel={() => setDeleteIds(null)}
          onConfirm={dryRun => handleDelete(deleteIds, dryRun)}
        />
      )}
    </div>
  );
}

// ── IP Search Tab ─────────────────────────────────────────────────────────────

interface IpSearchTabProps {
  subscriptionId: string;
  showToast: (msg: string, type?: 'success' | 'error' | 'info') => void;
}

function IpSearchTab({ subscriptionId, showToast }: IpSearchTabProps) {
  const [ip, setIp] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<IpSearchResult | null>(null);

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!ip.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post<IpSearchResult>(`${API_URL}/networksecurity/search/ip`, { ipAddress: ip.trim(), subscriptionId });
      setResult(res.data);
    } catch {
      showToast('IP search failed', 'error');
    } finally {
      setLoading(false);
    }
  }

  const allowCount = result ? [
    ...result.firewallMatches.filter(m => m.rule.action === 'Allow'),
    ...result.nsgMatches.filter(m => m.rule.access === 'Allow'),
  ].length : 0;

  const denyCount = result ? [
    ...result.firewallMatches.filter(m => m.rule.action === 'Deny'),
    ...result.nsgMatches.filter(m => m.rule.access === 'Deny'),
  ].length : 0;

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: '1rem' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
              IP Address or CIDR range (e.g. 10.1.2.3 or 192.168.0.0/24)
            </label>
            <input style={inputStyle} value={ip} onChange={e => setIp(e.target.value)} placeholder="10.1.0.0/24" />
          </div>
          <button type="submit" style={primaryBtn} disabled={loading || !ip.trim()}>
            {loading ? 'Searching...' : '🔍 Search'}
          </button>
        </form>
      </div>

      {result && (
        <>
          {/* Summary */}
          <div style={{ ...cardStyle, marginBottom: '1rem', display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>IP </span>
              <code style={{ color: 'var(--text-primary)' }}>{result.ipAddress}</code>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}> matched </span>
              <strong>{result.totalMatchCount}</strong>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}> rule{result.totalMatchCount !== 1 ? 's' : ''}</span>
            </div>
            {result.totalMatchCount > 0 && (
              <>
                <span style={{ color: '#28a745', fontWeight: 700 }}>✓ Allow: {allowCount}</span>
                <span style={{ color: '#dc3545', fontWeight: 700 }}>✗ Deny: {denyCount}</span>
              </>
            )}
            {result.totalMatchCount === 0 && (
              <span style={{ color: 'var(--text-secondary)' }}>No rules found matching this IP across all firewall policies and NSGs.</span>
            )}
          </div>

          {/* Firewall matches */}
          {result.firewallMatches.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ margin: '0 0 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                🔥 Firewall Policy Matches ({result.firewallMatches.length})
              </h4>
              <div style={cardStyle}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Policy</th>
                        <th style={thStyle}>RCG</th>
                        <th style={thStyle}>RC</th>
                        <th style={thStyle}>Rule</th>
                        <th style={thStyle}>Priority</th>
                        <th style={thStyle}>Match</th>
                        <th style={thStyle}>Action</th>
                        <th style={thStyle}>Risk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.firewallMatches.map((m, i) => (
                        <tr key={i}>
                          <td style={{ ...tdStyle, fontWeight: 500 }}>{m.policyName}</td>
                          <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{m.rcgName}</td>
                          <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{m.rcName}</td>
                          <td style={tdStyle}>{m.rule.name}</td>
                          <td style={tdStyle}><code>{m.rule.priority}</code></td>
                          <td style={tdStyle}>
                            {m.isSourceMatch && <span style={{ color: '#007bff', marginRight: '0.25rem' }}>src</span>}
                            {m.isDestinationMatch && <span style={{ color: '#6f42c1' }}>dst</span>}
                          </td>
                          <td style={tdStyle}>{actionBadge(m.rule.action)}</td>
                          <td style={tdStyle}>{riskBadge(m.risk)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* NSG matches */}
          {result.nsgMatches.length > 0 && (
            <div>
              <h4 style={{ margin: '0 0 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                🛡 NSG Matches ({result.nsgMatches.length})
              </h4>
              <div style={cardStyle}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>NSG</th>
                        <th style={thStyle}>Rule</th>
                        <th style={thStyle}>Priority</th>
                        <th style={thStyle}>Direction</th>
                        <th style={thStyle}>Match</th>
                        <th style={thStyle}>Access</th>
                        <th style={thStyle}>Risk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.nsgMatches.map((m, i) => (
                        <tr key={i}>
                          <td style={{ ...tdStyle, fontWeight: 500 }}>{m.nsgName}</td>
                          <td style={tdStyle}>{m.rule.name}</td>
                          <td style={tdStyle}><code>{m.rule.priority}</code></td>
                          <td style={tdStyle}>{m.rule.direction}</td>
                          <td style={tdStyle}>
                            {m.isSourceMatch && <span style={{ color: '#007bff', marginRight: '0.25rem' }}>src</span>}
                            {m.isDestinationMatch && <span style={{ color: '#6f42c1' }}>dst</span>}
                          </td>
                          <td style={tdStyle}>{actionBadge(m.rule.access)}</td>
                          <td style={tdStyle}>{riskBadge(m.risk)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

type SubTab = 'firewall' | 'nsg' | 'ipsearch';

export default function NetworkSecurity() {
  const { showToast: addToast } = useToast();
  const [subTab, setSubTab] = useState<SubTab>('firewall');
  const [subscriptionId] = useState('all');
  const [policies, setPolicies] = useState<FirewallPolicyDto[]>([]);
  const [nsgs, setNsgs] = useState<NsgDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const showToast = useCallback((msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    addToast(msg, type === 'error' ? 'error' : type === 'success' ? 'success' : 'info');
  }, [addToast]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [fwRes, nsgRes] = await Promise.all([
        axios.get<FirewallPolicyDto[]>(`${API_URL}/networksecurity/firewall/policies`, { params: { subscriptionId } }),
        axios.get<NsgDto[]>(`${API_URL}/networksecurity/nsgs`, { params: { subscriptionId } }),
      ]);
      setPolicies(fwRes.data);
      setNsgs(nsgRes.data);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to load network security data';
      setError(msg);
      addToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [subscriptionId, addToast]);

  useEffect(() => { loadData(); }, [loadData]);

  const subTabs: { id: SubTab; label: string }[] = [
    { id: 'firewall', label: '🔥 Firewall Policies' },
    { id: 'nsg', label: '🛡 NSG Rules' },
    { id: 'ipsearch', label: '🔍 IP Search' },
  ];

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem' }}>🛡️ Network Security</h2>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Manage Azure Firewall Policy rules and Network Security Group rules across your subscriptions
          </p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid var(--border-color)', marginBottom: '1.5rem' }}>
        {subTabs.map(t => (
          <button
            key={t.id}
            className={`tab-btn ${subTab === t.id ? 'active' : ''}`}
            onClick={() => setSubTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
          Loading network security data...
        </div>
      )}

      {error && !loading && (
        <div style={{ ...cardStyle, borderColor: 'var(--danger-color)', color: 'var(--danger-color)', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {subTab === 'firewall' && policies.length === 0 && (
            <div style={{ ...cardStyle, color: 'var(--text-secondary)' }}>No firewall policies found.</div>
          )}
          {subTab === 'firewall' && policies.length > 0 && (
            <FirewallTab policies={policies} subscriptionId={subscriptionId} onRefresh={loadData} showToast={showToast} />
          )}

          {subTab === 'nsg' && nsgs.length === 0 && (
            <div style={{ ...cardStyle, color: 'var(--text-secondary)' }}>No NSGs found.</div>
          )}
          {subTab === 'nsg' && nsgs.length > 0 && (
            <NsgTab nsgs={nsgs} subscriptionId={subscriptionId} onRefresh={loadData} showToast={showToast} />
          )}

          {subTab === 'ipsearch' && (
            <IpSearchTab subscriptionId={subscriptionId} showToast={showToast} />
          )}
        </>
      )}
    </div>
  );
}
